import React from 'react';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import DefinitionsPanel from './DefinitionsPanel';
import { formatEuro } from '../../utils/money';

const KpiPopoverItem = ({ label, value, onClick, breakdown, infoText, testid, infoKey }) => {
  // value may be a plain string/number OR an object {mine, total} for "mine / total" format
  const isSplit = value && typeof value === 'object' && 'mine' in value && 'total' in value;
  const finalTestid = testid || `kpi-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-baseline gap-1.5 whitespace-nowrap hover:bg-muted/60 rounded px-1.5 py-0.5 transition-colors"
          data-testid={finalTestid}
          onClick={onClick}
        >
          <span className="text-xs text-muted-foreground">{label}</span>
          {isSplit ? (
            <span className="flex items-baseline gap-0.5">
              <span className="text-sm font-semibold text-foreground">{value.mine}</span>
              <span className="text-xs text-muted-foreground">/</span>
              <span className="text-[11px] text-muted-foreground">{value.total}</span>
            </span>
          ) : (
            <span className="text-sm font-semibold">{value}</span>
          )}
          {infoText && (
            <span
              role="button"
              tabIndex={0}
              className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded cursor-help"
              aria-label={`Información sobre ${label}`}
              data-testid={infoKey ? `info-${infoKey}` : undefined}
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="w-3 h-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</p>
        {infoText && (
          <p className="text-[10px] text-muted-foreground italic mb-2">{infoText}</p>
        )}
        <div className="space-y-1">
          {breakdown.map((b) => (
            <div key={b.label} className="flex items-center justify-between text-xs">
              <span className={b.colorClass || 'text-muted-foreground'}>{b.label}</span>
              <span className="font-semibold">{b.value}</span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const CompactKPIBar = ({ kpis, userName, generatedAt, users, currentUserRole, asUserId, onAsUserChange, isImpersonating, impersonatedName }) => {
  if (!kpis) return null;
  const wonYear = kpis.won_ytd_year || new Date().getFullYear();
  // Format "mine / total" (mine highlighted, total muted). Handles null (show "-").
  const mineOverTotal = (mine, total, formatter = (v) => v) => ({
    mine: mine === null || mine === undefined ? '-' : formatter(mine),
    total: total === null || total === undefined ? '-' : formatter(total),
  });
  const eurOrDash = (v) => (v === null || v === undefined || v === 0 ? '-' : formatEuro(v));
  const items = [
    {
      label: 'Empresas',
      value: mineOverTotal(kpis.my_companies_active ?? 0, kpis.active_companies ?? kpis.total_companies ?? 0),
      breakdown: [
        { label: 'TYPSA', value: kpis.typsa_units || 0, colorClass: 'text-red-700' },
        { label: 'Privadas', value: kpis.companies_privada || 0, colorClass: 'text-purple-700' },
        { label: 'Públicas', value: kpis.companies_publica || 0, colorClass: 'text-green-700' },
        { label: 'Total', value: (kpis.typsa_units || 0) + (kpis.companies_privada || 0) + (kpis.companies_publica || 0), colorClass: 'text-foreground font-semibold border-t pt-1 mt-1' },
      ],
      testid: 'kpi-empresas',
      infoKey: 'kpi-empresas',
      infoText: 'Empresas activas de las que eres responsable / empresas activas totales del CRM.',
    },
    {
      label: 'Contactos',
      value: mineOverTotal(kpis.my_contacts_total ?? 0, kpis.total_contacts ?? 0),
      breakdown: [
        { label: 'TYPSA', value: kpis.contacts_typsa || 0, colorClass: 'text-red-700' },
        { label: 'Privadas', value: kpis.contacts_privada || 0, colorClass: 'text-purple-700' },
        { label: 'Públicas', value: kpis.contacts_publica || 0, colorClass: 'text-green-700' },
        { label: 'Total', value: (kpis.contacts_typsa || 0) + (kpis.contacts_privada || 0) + (kpis.contacts_publica || 0), colorClass: 'text-foreground font-semibold border-t pt-1 mt-1' },
      ],
      testid: 'kpi-contactos',
      infoKey: 'kpi-contactos',
      infoText: 'Contactos de tu cartera / contactos totales del CRM.',
    },
    {
      label: 'Oportunidades a trabajar',
      value: mineOverTotal(kpis.my_opps_actionable ?? 0, kpis.opps_actionable ?? 0),
      breakdown: [
        { label: 'Detectadas (mías)', value: kpis.my_opps_detectada || 0 },
        { label: 'Propuestas (mías)', value: kpis.my_opps_propuesta || 0 },
        { label: 'Detectadas (total)', value: kpis.active_opps_detectada || 0 },
        { label: 'Propuestas (total)', value: kpis.active_opps_propuesta || 0 },
        { label: 'Total', value: (kpis.active_opps_detectada || 0) + (kpis.active_opps_propuesta || 0), colorClass: 'text-foreground font-semibold border-t pt-1 mt-1' },
      ],
      testid: 'kpi-oportunidades-activas',
      infoKey: 'kpi-oportunidades',
      infoText: 'Oportunidades en Detectada (prospecto) o Propuesta (en preparación): las únicas que requieren acción comercial desde este panel. Tuyas / totales del CRM.',
    },
    {
      label: 'Ganadas',
      value: mineOverTotal(kpis.my_won_ytd_tds ?? 0, kpis.won_ytd_tds ?? 0, eurOrDash),
      breakdown: [
        { label: 'Mío (asignado a TDS)', value: formatEuro(kpis.my_won_ytd_tds || 0) },
        { label: 'Total (asignado a TDS)', value: formatEuro(kpis.won_ytd_tds || 0) },
        { label: '—', value: '' },
        { label: 'TYPSA (importe ofertado)', value: formatEuro(kpis.won_ytd_typsa || 0), colorClass: 'text-red-700' },
        { label: 'Privadas (importe ofertado)', value: formatEuro(kpis.won_ytd_privada || 0), colorClass: 'text-purple-700' },
        { label: 'Públicas (importe ofertado)', value: formatEuro(kpis.won_ytd_publica || 0), colorClass: 'text-green-700' },
        { label: 'Total ofertado', value: formatEuro((kpis.won_ytd_typsa || 0) + (kpis.won_ytd_privada || 0) + (kpis.won_ytd_publica || 0)), colorClass: 'text-foreground font-semibold border-t pt-1 mt-1' },
      ],
      testid: 'kpi-ganadas',
      infoKey: 'kpi-ganadas',
      infoText: `Suma de importe ofertado de oportunidades ganadas desde el 01/01/${wonYear}. TDS es la parte del importe ofertado que factura TDS. Si aparece "-" es que no hay importe TDS registrado.`,
    },
  ];

  return (
    <div
      className="flex flex-wrap md:flex-nowrap items-center justify-between gap-2 px-3 py-2 rounded-md border bg-muted/30"
      data-testid="compact-kpi-bar"
    >
      <div className="flex gap-3 md:gap-5 overflow-x-auto flex-1 min-w-0 scrollbar-thin">
        {items.map((it) => (
          <KpiPopoverItem
            key={it.label}
            label={it.label}
            value={it.value}
            breakdown={it.breakdown}
            infoText={it.infoText}
            testid={it.testid}
            infoKey={it.infoKey}
          />
        ))}
      </div>
      <div className="hidden md:flex items-center gap-3 text-[11px] text-muted-foreground flex-shrink-0">
        <DefinitionsPanel />
        {isImpersonating && (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]" data-testid="impersonation-badge">
            Vista de {impersonatedName || 'otro'} · solo lectura
          </Badge>
        )}
        {(currentUserRole || '').toLowerCase() === 'admin' && Array.isArray(users) && users.length > 0 && (
          <Select value={asUserId || '__self__'} onValueChange={(v) => onAsUserChange(v === '__self__' ? null : v)}>
            <SelectTrigger className="h-7 text-[11px] w-[190px]" data-testid="dashboard-user-switcher">
              <SelectValue placeholder="Ver panel de..." />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="__self__">Mi panel</SelectItem>
              {users.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(u => (
                <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {isImpersonating && (
          <button
            type="button"
            onClick={() => onAsUserChange(null)}
            className="text-[11px] text-blue-700 underline"
            data-testid="back-to-my-panel-btn"
          >
            Volver a mi panel
          </button>
        )}
        {generatedAt && (
          <span>Actualizado {new Date(generatedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
        )}
        {userName && <span className="font-medium text-foreground">{userName}</span>}
      </div>
    </div>
  );
};

export default CompactKPIBar;
